import React from "react"
import PropTypes from "prop-types"
import { AuthCard } from "components/views/login/Shared"
import { HR, Spacer, FlexRowCenter } from "../atoms/Atoms"
import { AuthButton, AuthLayout } from "./Shared"
import { useFormContext } from "react-hook-form"
import L10n from "../../../lib/carrot-l10n"
import ErrorMessageMarkdown from "./ErrorMessageMarkdown"
import { ArrowLink } from "../atoms/links/ArrowLink"
import { CompleteFormInput } from "components/views/atoms/forms/FormInput"
import { Body } from "../atoms/Bodies"
import { useLocation } from "react-router-dom"
import { H1 } from "@carrotfertility/carotene"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const EmailInput = ({ enabled }) => {
  const { register, errors } = useFormContext()
  return (
    <CompleteFormInput
      readOnly={!enabled}
      ref={register({ required: "Email is required" })}
      showValidation={false}
      errors={errors}
      name="email"
      label="Email"
      type="email"
    />
  )
}

const PasswordInput = ({ passwordInputRef, showPasswordInput }) => {
  const { errors, register } = useFormContext()
  return (
    <CompleteFormInput
      ref={(r) => {
        showPasswordInput && register(r, { required: "Password is required" })
        passwordInputRef.current = r
      }}
      errors={errors}
      showValidation={false}
      id="password"
      name="password"
      label={l`client.password`}
      type="password"
    />
  )
}

const LoginHeader = () => {
  const urlSearchParams = new URLSearchParams(useLocation().search)
  const returnToSupportCenter = urlSearchParams.get("return_to")
  return (
    <>
      <FlexRowCenter>
        <H1>{l`client.welcomeMessage`}</H1>
      </FlexRowCenter>
      <Spacer height="0.5" />
      <div className="flex flex-auto flex-column items-center justify-center">
        {!returnToSupportCenter && (
          <>
            <Body color="black-80">{l`Sign in with your company email`}</Body>
            <Body color="black-80">{l`address to continue`}</Body>
          </>
        )}
        {returnToSupportCenter && (
          <>
            <Body color="black-80">{l`Sign in to your account to access`}</Body>
            <Body color="black-80">{l`the Support Center.`}</Body>
          </>
        )}
      </div>
    </>
  )
}

const LoginButton = ({ onClick, submitting }) => (
  <div className="flex flex-auto justify-center tc">
    <AuthButton onClick={onClick}>{submitting ? l`client.loggingIn` : l`client.login`}</AuthButton>
  </div>
)

const LoginFooter = ({ showForgotPassword }) => {
  return (
    <>
      <Spacer height={1.5} />
      <HR color="light-gray" />
      <Spacer height={0.75} />
      <div className="flex flex-auto flex-column items-center justify-center">
        <ArrowLink to="/signup">{l`client.signUp.newToCarrotSignup`}</ArrowLink>
        {showForgotPassword && (
          <>
            <Spacer height={0.75} />
            <ArrowLink to={`/request-reset-password`}>{l`client.forgotPassword`}</ArrowLink>
          </>
        )}
      </div>
    </>
  )
}

const Login = ({
  errorMessage,
  onSubmit,
  passwordInputRef,
  shake,
  showEmailInput,
  showForgotPassword,
  showPasswordInput,
  submitting
}) => (
  <AuthLayout title={l`client.login`}>
    <AuthCard id="login-card" shake={shake} onSubmit={onSubmit}>
      <Spacer height={4} />
      {errorMessage ? <ErrorMessageMarkdown source={errorMessage} /> : <LoginHeader />}
      <Spacer height={1.5} />
      {showEmailInput && <EmailInput enabled={!showPasswordInput} />}
      <div
        style={{
          maxHeight: showPasswordInput ? null : 0,
          overflow: showPasswordInput ? null : "hidden"
        }}
      >
        <Spacer height={1.5} />
        <PasswordInput passwordInputRef={passwordInputRef} showPasswordInput={showPasswordInput} />
      </div>
      <Spacer height={1.5} />
      <LoginButton onClick={onSubmit} submitting={submitting} />
      <LoginFooter showForgotPassword={showForgotPassword} />
    </AuthCard>
  </AuthLayout>
)

Login.propTypes = {
  errorMessage: PropTypes.string,
  login: PropTypes.func.isRequired,
  lookup: PropTypes.func.isRequired,
  showForgotPassword: PropTypes.bool.isRequired,
  showEmailInput: PropTypes.bool.isRequired,
  showPasswordInput: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  username: PropTypes.string,
  shake: PropTypes.bool.isRequired
}

export default Login
